import {
    AMC_SCAN_NOT_PROVIDED,
    CREDIT_AUTHORIZATION_NOT_PROVIDED,
    CREDIT_AUTHORIZATION_REFUSED,
    EDITABLE_FIELDS,
    MERGEABLE_FIELDS,
    PATIENT_STATUS_LABELS,
} from "./administrationConstants";

export function valuesEqual(left, right) {
    return (left ?? null) === (right ?? null);
}

export function displayMergeValue(value) {
    if (value === null || value === undefined || value === "") return "Aucune valeur";
    if (value === true) return "Oui";
    if (value === false) return "Non";
    return String(value);
}

export function mergeAdministrationDraft(base, local, remote) {
    const merged = { ...remote, version: remote.version };
    const conflicts = [];

    MERGEABLE_FIELDS.forEach(field => {
        const locallyChanged = !valuesEqual(local[field], base[field]);
        const remotelyChanged = !valuesEqual(remote[field], base[field]);
        if (locallyChanged && remotelyChanged && !valuesEqual(local[field], remote[field])) {
            conflicts.push(field);
        } else if (locallyChanged) {
            merged[field] = local[field];
        }
    });

    return { merged, conflicts };
}

export function formatDate(date) {
    if (!date) return "—";
    const [year, month, day] = date.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
}

export function formatDateTime(value) {
    if (!value) return "";
    return new Date(value).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function toDateInputValue(date) {
    return date ? date.slice(0, 10) : "";
}

export function formatPatientName(row) {
    const firstName = row.prenom
        ? row.prenom.charAt(0).toUpperCase() + row.prenom.slice(1).toLowerCase()
        : "";
    return `${row.nom || ""} ${firstName}`.trim();
}

export function normalizePatientStatus(value) {
    return PATIENT_STATUS_LABELS[value] ? value : "AUCUN";
}

export function normalizeCreditAuthorization(rowOrValue) {
    if (typeof rowOrValue === "object" && rowOrValue !== null) {
        if (rowOrValue.creditAuthorization) return normalizeCreditAuthorization(rowOrValue.creditAuthorization);
        if (rowOrValue.creditAuthorizationType === "AMOUNT" && rowOrValue.creditAuthorizationAmount != null) return "Oui";
        return normalizeCreditAuthorization(rowOrValue.creditAuthorizationType);
    }
    const normalized = String(rowOrValue || "").trim().toLowerCase();
    if (!normalized) return CREDIT_AUTHORIZATION_NOT_PROVIDED;
    if (["non renseigné", "non renseigne", "not_provided", "not provided", "unknown"].includes(normalized)) {
        return CREDIT_AUTHORIZATION_NOT_PROVIDED;
    }
    if (["non", "no", "false", "0", "refus", "refused", "none", "aucun"].includes(normalized)) return CREDIT_AUTHORIZATION_REFUSED;
    return "Oui";
}

export function normalizeGuardianContactStatus(value) {
    const normalized = String(value || "").trim().toUpperCase();
    if (!normalized) return null;
    if (["NONE", "COMPLETE", "EMAIL", "EMAIL_ADDRESS"].includes(normalized)) return normalized;
    if (["ADDRESS", "ADRESSE"].includes(normalized)) return "EMAIL_ADDRESS";
    if (["PHONE", "TELEPHONE", "TÉLÉPHONE"].includes(normalized)) return "EMAIL";
    if (["DECEASED", "DCD"].includes(normalized)) return "NONE";
    return null;
}

export function administrationPayload(row) {
    const payload = Object.fromEntries(EDITABLE_FIELDS.map(field => [field, row[field] ?? null]));
    const creditAuthorization = normalizeCreditAuthorization(row);
    const creditAuthorizationAmount = normalizeCreditAuthorizationAmount(row.creditAuthorizationAmount);
    payload.patientStatus = normalizePatientStatus(row.patientStatus);
    payload.creditAuthorization = creditAuthorization;
    payload.creditAuthorizationType = creditAuthorization === "Oui" && creditAuthorizationAmount !== null ? "AMOUNT" : null;
    payload.creditAuthorizationAmount = payload.creditAuthorizationType === "AMOUNT" ? creditAuthorizationAmount : null;
    payload.amcScanReceived = normalizeAmcScanReceived(row.amcScanReceived);
    if (!isAmcDateEnabled(payload.amcScanReceived)) payload.amcDate = null;
    payload.guardianContactStatus = normalizeGuardianContactStatus(row.guardianContactStatus);
    return payload;
}

export function normalizeCreditAuthorizationAmount(value) {
    if (value === null || value === undefined || value === "") return null;
    const amount = Number(String(value).replace(",", "."));
    return Number.isFinite(amount) ? amount : null;
}

export function latestEvent(row, type, vaccination = false) {
    const events = vaccination ? row.vaccinationEvents : row.billingEvents;
    return (events || []).find(event => event.type === type);
}

export function normalizeAmcScanReceived(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return AMC_SCAN_NOT_PROVIDED;
    if (["non renseigné", "non renseigne", "not_provided", "not provided", "unknown"].includes(normalized)) {
        return AMC_SCAN_NOT_PROVIDED;
    }
    if (["non", "no", "false", "0"].includes(normalized)) return "Non";
    return "Oui";
}

export function isAmcDateEnabled(value) {
    return normalizeAmcScanReceived(value) === "Oui";
}

export function isAutomaticDebitAuthorizationEnabled(value) {
    return normalizeAmcScanReceived(value) === "Oui";
}

export function parseGuardianContactValue(value) {
    const text = String(value || "");
    const result = { nom: "", prenom: "", mail: "", adresse: "" };
    text.split(/\r?\n/).forEach(line => {
        const [rawKey, ...rest] = line.split(":");
        const key = String(rawKey || "").trim().toLowerCase();
        const fieldValue = rest.join(":").trim();
        if (["nom"].includes(key)) result.nom = fieldValue;
        if (["prénom", "prenom"].includes(key)) result.prenom = fieldValue;
        if (["mail", "email", "e-mail"].includes(key)) result.mail = fieldValue;
        if (["adresse"].includes(key)) result.adresse = fieldValue;
    });
    if (!Object.values(result).some(Boolean) && text.trim()) result.adresse = text.trim();
    return result;
}

export function formatGuardianContactValue(contact) {
    return [
        ["Nom", contact.nom],
        ["Prénom", contact.prenom],
        ["Mail", contact.mail],
        ["Adresse", contact.adresse],
    ]
        .map(([label, value]) => [label, String(value || "").trim()])
        .filter(([, value]) => value)
        .map(([label, value]) => `${label}: ${value}`)
        .join("\n");
}

export function productName(product) {
    return [product.nom, product.dosage].filter(Boolean).join(" ") || "Produit sans nom";
}

export function normalizeSearchText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}
